/**
 * 图片格式支持检测（WebP + AVIF）
 *
 * 检测浏览器是否支持 WebP / AVIF，结果缓存到 localStorage（7 天）。
 * 支持 forceFallback 调试降级（所有环境生效，方便回归和线上验证）。
 *
 * 状态分层：
 *   forceFallback  → localStorage + Cookie 双写
 *                    （Cookie 供未来 SSR 场景读取）
 *   格式检测结果   → 内存缓存 + localStorage 双层
 *                    页面刷新后内存重置为 null，readCache 自动从 localStorage 恢复
 */

const WEBP_STORAGE_KEY = 'umu_webp_support';
const AVIF_STORAGE_KEY = 'umu_avif_support';
const STORAGE_TIMESTAMP_SUFFIX = '_ts';
const FORCE_FALLBACK_KEY = 'umu_avif_webp_force_fallback';
const FORCE_FALLBACK_URL_PARAM = '_avif_webp_fallback';
const AVIF_NS_COOKIE = 'umu_avif_ns';
const WEBP_NS_COOKIE = 'umu_webp_ns';
export const AVIF_OK_COOKIE = 'umu_avif_ok';
export const WEBP_OK_COOKIE = 'umu_webp_ok';
const COOKIE_MAX_AGE = 7 * 24 * 3600;
const DETECT_TIMEOUT = 3000;
const CACHE_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000;

// 按当前站点的 CDN 域名动态构建探针 URL，与 service-pages 使用各自 CDN_URL 的方式一致
// 前置条件：需在各站点 CDN 上传 statics/umu_home/probe/webp-test.webp 和 avif-test.avif
const CDN_DOMAIN_MAP: Record<string, string> = {
    cn: 'statics-cdn-cn.umucdn.cn',
    en: 'd1bvk99i2a79wx.cloudfront.net',
    em: 'dmks9efzatfsk.cloudfront.net',
    jp: 'dpfrzbu0cqnmm.cloudfront.net',
    tw: 'd1yemi1ss2f2oh.cloudfront.net',
};
const PROBE_BASE = `https://${CDN_DOMAIN_MAP[SYSTEM.toLowerCase()] ?? CDN_DOMAIN_MAP.cn}/statics/service-pages/images-support-check/`;
const WEBP_TEST_URL = `${PROBE_BASE}webp-test.webp`;
const AVIF_TEST_URL = `${PROBE_BASE}avif-test.avif`;

// forceFallback 模拟使用不存在的资源，让浏览器发出真实请求并失败（Network 面板可见）
const WEBP_TEST_URL_NOT = `${PROBE_BASE}webp-not-exist.webp`;
const AVIF_TEST_URL_NOT = `${PROBE_BASE}avif-not-exist.avif`;

let webpCached: boolean | null = null;
let avifCached: boolean | null = null;
let webpPromise: Promise<boolean> | null = null;
let avifPromise: Promise<boolean> | null = null;

let _synced = false;

// ---- 格式支持 Cookie（供 headerContent 内联脚本在 SSR/静态 HTML 中读取）----

function setFormatSupportCookie(nsCookieName: string, supported: boolean): void {
    const okCookieName = nsCookieName === AVIF_NS_COOKIE ? AVIF_OK_COOKIE : WEBP_OK_COOKIE;
    try {
        if (supported) {
            document.cookie = `${nsCookieName}=;path=/;max-age=0`;
            document.cookie = `${okCookieName}=1;path=/;max-age=${COOKIE_MAX_AGE}`;
        } else {
            document.cookie = `${nsCookieName}=1;path=/;max-age=${COOKIE_MAX_AGE}`;
            document.cookie = `${okCookieName}=;path=/;max-age=0`;
        }
    } catch {}
}

// ---- forceFallback ----

function setForceFallbackCookie(active: boolean): void {
    try {
        if (active) {
            document.cookie = `${FORCE_FALLBACK_KEY}=1;path=/;max-age=${COOKIE_MAX_AGE}`;
        } else {
            document.cookie = `${FORCE_FALLBACK_KEY}=;path=/;max-age=0`;
        }
    } catch {}
}

function syncForceFallbackState(): void {
    if (_synced) return;
    _synced = true;
    try {
        const val = new URLSearchParams(window.location.search).get(FORCE_FALLBACK_URL_PARAM);
        if (val === '1') {
            localStorage.setItem(FORCE_FALLBACK_KEY, '1');
            setForceFallbackCookie(true);
            return;
        }
        if (val === '0') {
            localStorage.removeItem(FORCE_FALLBACK_KEY);
            setForceFallbackCookie(false);
            return;
        }
    } catch {}
    try {
        setForceFallbackCookie(localStorage.getItem(FORCE_FALLBACK_KEY) === '1');
    } catch {}
}

export function isForceFallbackActive(): boolean {
    if (typeof window === 'undefined') return false;
    syncForceFallbackState();
    try {
        return localStorage.getItem(FORCE_FALLBACK_KEY) === '1';
    } catch {
        return false;
    }
}

export { FORCE_FALLBACK_URL_PARAM };

// ---- localStorage 读写 ----

function readCache(storageKey: string, memCached: boolean | null): boolean | null {
    if (memCached !== null) return memCached;
    if (typeof window === 'undefined') return null;
    try {
        const stored = localStorage.getItem(storageKey);
        const timestamp = localStorage.getItem(storageKey + STORAGE_TIMESTAMP_SUFFIX);
        if (stored === null || timestamp === null) return null;
        const age = Date.now() - Number(timestamp);
        if (age > CACHE_EXPIRY_MS) {
            localStorage.removeItem(storageKey);
            localStorage.removeItem(storageKey + STORAGE_TIMESTAMP_SUFFIX);
            return null;
        }
        return stored === '1';
    } catch {
        return null;
    }
}

function writeCache(storageKey: string, supported: boolean): void {
    try {
        localStorage.setItem(storageKey, supported ? '1' : '0');
        localStorage.setItem(storageKey + STORAGE_TIMESTAMP_SUFFIX, String(Date.now()));
    } catch {}
}

// ---- 通用检测 ----

function detectFormat(
    testUrl: string,
    storageKey: string,
    getCached: () => boolean | null,
    setCached: (v: boolean) => void,
    getPromise: () => Promise<boolean> | null,
    setPromise: (p: Promise<boolean>) => void,
    simulation = false,
    onPersistResult?: (supported: boolean) => void,
): Promise<boolean> {
    if (simulation) {
        return new Promise<boolean>((resolve) => {
            const timer = setTimeout(() => resolve(false), DETECT_TIMEOUT);
            const img = new Image();
            img.onload = () => {
                clearTimeout(timer);
                resolve(false);
            };
            img.onerror = () => {
                clearTimeout(timer);
                resolve(false);
            };
            img.src = testUrl;
        });
    }

    const sync = getCached();
    if (sync !== null) return Promise.resolve(sync);

    if (typeof window === 'undefined') return Promise.resolve(true);

    const existing = getPromise();
    if (existing) return existing;

    const promise = new Promise<boolean>((resolve) => {
        let settled = false;

        const settle = (supported: boolean, persist: boolean) => {
            if (settled) return;
            settled = true;
            if (supported) {
                setCached(true);
                if (persist) {
                    writeCache(storageKey, true);
                    onPersistResult?.(true);
                }
            } else {
                const current = getCached();
                if (current !== true) {
                    setCached(false);
                    if (persist) {
                        writeCache(storageKey, false);
                        onPersistResult?.(false);
                    }
                }
            }
            resolve(getCached() ?? supported);
        };

        const timer = setTimeout(() => settle(false, false), DETECT_TIMEOUT);
        const img = new Image();
        img.onload = () => {
            clearTimeout(timer);
            settle(img.width > 0 && img.height > 0, true);
        };
        img.onerror = () => {
            clearTimeout(timer);
            settle(false, true);
        };
        img.src = testUrl;
    });

    setPromise(promise);
    return promise;
}

// ---- WebP ----

export function getWebpSupportSync(): boolean | null {
    if (isForceFallbackActive()) return false;
    const cached = readCache(WEBP_STORAGE_KEY, webpCached);
    if (cached !== null) webpCached = cached;
    return cached;
}

export function detectWebpSupport(): Promise<boolean> {
    const simulation = isForceFallbackActive();
    // 同步 localStorage 缓存 → Cookie，供 headerContent 内联脚本下次访问读取
    const cached = readCache(WEBP_STORAGE_KEY, webpCached);
    if (cached !== null) {
        webpCached = cached;
        setFormatSupportCookie(WEBP_NS_COOKIE, cached);
    }
    return detectFormat(
        simulation ? WEBP_TEST_URL_NOT : WEBP_TEST_URL,
        WEBP_STORAGE_KEY,
        () => getWebpSupportSync(),
        (v) => {
            webpCached = v;
        },
        () => webpPromise,
        (p) => {
            webpPromise = p;
        },
        simulation,
        (supported) => setFormatSupportCookie(WEBP_NS_COOKIE, supported),
    );
}

export function isWebpDetectionComplete(): boolean {
    return webpCached !== null;
}

// ---- AVIF ----

export function getAvifSupportSync(): boolean | null {
    if (isForceFallbackActive()) return false;
    const cached = readCache(AVIF_STORAGE_KEY, avifCached);
    if (cached !== null) avifCached = cached;
    return cached;
}

export function detectAvifSupport(): Promise<boolean> {
    const simulation = isForceFallbackActive();
    // 同步 localStorage 缓存 → Cookie，供 headerContent 内联脚本下次访问读取
    const cached = readCache(AVIF_STORAGE_KEY, avifCached);
    if (cached !== null) {
        avifCached = cached;
        setFormatSupportCookie(AVIF_NS_COOKIE, cached);
    }
    return detectFormat(
        simulation ? AVIF_TEST_URL_NOT : AVIF_TEST_URL,
        AVIF_STORAGE_KEY,
        () => getAvifSupportSync(),
        (v) => {
            avifCached = v;
        },
        () => avifPromise,
        (p) => {
            avifPromise = p;
        },
        simulation,
        (supported) => setFormatSupportCookie(AVIF_NS_COOKIE, supported),
    );
}

export function isAvifDetectionComplete(): boolean {
    return avifCached !== null;
}

// ---- 正向标记（图片 onload 确认格式可用）----

export function markWebpSupported(): void {
    if (webpCached === true) return;
    webpCached = true;
    writeCache(WEBP_STORAGE_KEY, true);
    setFormatSupportCookie(WEBP_NS_COOKIE, true);
}

export function markAvifSupported(): void {
    if (avifCached === true) return;
    avifCached = true;
    writeCache(AVIF_STORAGE_KEY, true);
    setFormatSupportCookie(AVIF_NS_COOKIE, true);
}
